import { logger } from "@jslib/shared/utils/logger";

import { QUERY_STRING_INSTALL_PAGE_URL } from "../../constants";
import { getOptions } from "../../options/options";
import { type RewardDisplayEnum } from "../types/runtimeTypes";
import { type DatabaseId, type Locale, type VerificationStep } from "../types/types";

export const getVerificationUrl = () =>
  `${getOptions().restApi.serviceUrl}${getOptions().restApi.resources.verification}`;
export const getVerificationStatusUrl = (verificationId: DatabaseId) =>
  `${getVerificationUrl()}${verificationId}`;
export const getNewVerificationRequestUrl = () => getVerificationUrl();
export const getProgramThemeUrl = (programId: DatabaseId, locale: Locale = null) => {
  const baseUrl = `${getOptions().restApi.serviceUrl}${
    getOptions().restApi.resources.program.base
  }${programId}/${getOptions().restApi.resources.program.theme}`;
  if (locale) {
    return `${baseUrl}?locale=${locale}`;
  }
  return `${baseUrl}`;
};

export const getNewSmsCodeResendUrl = (verificationId: DatabaseId) =>
  `${getOptions().restApi.serviceUrl}rest/v2/verification/${verificationId}/step/smsLoop/retry`;
export const getNewEmailCodeResendUrl = (verificationId: DatabaseId) =>
  `${getOptions().restApi.serviceUrl}rest/v2/verification/${verificationId}/step/emailLoop/retry`;

export const getFieldsToCollectUrl = (verificationId: DatabaseId, stepName: VerificationStep) =>
  `${getOptions().restApi.serviceUrl}rest/v2/verification/${verificationId}/${stepName}/fields`;

const MILITARY_STEPS_USING_UNIFIED_ENDPOINT = new Set<VerificationStep>([
  "collectMilitaryStatus",
  "collectActiveMilitaryPersonalInfo",
  "collectInactiveMilitaryPersonalInfo",
]);

/**
 * The three legacy military `currentStep` values from the server all submit
 * to a single unified endpoint, `step/collectMilitaryPersonalInfo`. The
 * server still returns its old submissionUrl for backwards compatibility,
 * so we ignore it for these three steps and build the unified URL ourselves.
 */
export const getEffectiveSubmissionUrl = (
  step: VerificationStep,
  previousResponse: { submissionUrl?: string },
  verificationId: DatabaseId,
): string => {
  if (MILITARY_STEPS_USING_UNIFIED_ENDPOINT.has(step)) {
    return `${getOptions().restApi.serviceUrl}rest/v2/verification/${verificationId}/step/collectMilitaryPersonalInfo`;
  }
  return previousResponse.submissionUrl;
};

export const getDocTypeForCountriesUrl = (verificationId: DatabaseId, countries: string[]) =>
  `${
    getOptions().restApi.serviceUrl
  }rest/v2/verification/${verificationId}/step/idCheckLoop/getDocumentTypesForCountries?countries=${countries}`;

export const getBarcodeUrl = (
  verificationId: DatabaseId,
  rewardKey: string,
  symbology: RewardDisplayEnum,
) =>
  `${
    getOptions().restApi.serviceUrl
  }rest/v2/verification/${verificationId}/barcode?rewardKey=${rewardKey}&symbology=${symbology}`;

/**
 * Get the version that was actually used
 * The build process replaces this with the value.
 * e.g. 1.59.0 or 1.46.0-alpha.575
 */
export const getPublishedVersion = (): string => process.env.NEXT_VERSION;

export const getCurrentUrl = (): string => window.location.href;

export const getInstallPageUrl = (): string | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_INSTALL_PAGE_URL)) {
      return decodeURIComponent(parsed.get(QUERY_STRING_INSTALL_PAGE_URL));
    }
    if (getOptions()[QUERY_STRING_INSTALL_PAGE_URL]) {
      return getOptions()[QUERY_STRING_INSTALL_PAGE_URL];
    }
  } catch (e) {
    logger.error(e, "Install Page URL");
  }

  return getCurrentUrl();
};

export { getInstallType, InstallTypes, type InstallType } from "@jslib/shared/utils/getInstallType";

export const getVerificationHelpUrl = (): string =>
  `${getOptions().restApi.serviceUrl}rest/v2/help/verificationHelp`;
